import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  LinearProgress,
  Divider,
  Switch,
  Icon,
  Checkbox,
  Chip,
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  DatePicker
} from '@material-ui/pickers';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { Formik } from 'formik';
import * as yup from 'yup';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import NumberFormat from 'react-number-format';
import { format, parseISO, isValid } from 'date-fns';
import {
  createCouponRuleCombinedApi,
  getStoreCategoriesApi,
  getStoreProductsApi,
  getStoreCustomerGroupsApi,
} from 'api';
import { showNotification } from 'redux/actions/ui';
import { BackdropCircular, InfoTooltip } from 'components';
import CouponRuleSummary from './CouponRuleSummary';


const useStyles = makeStyles({
  custom_input: {
    '& .MuiOutlinedInput-root.MuiOutlinedInput-root.MuiOutlinedInput-root .MuiOutlinedInput-input': {
      padding: '7px 10px'
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-marginDense': {
      transform: 'translate(14px, 8px) scale(1)'
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(12px, -6px) scale(0.75)'
    },
    '& .MuiFormHelperText-contained': {
      marginLeft: '0',
      marginRight: '0'
    }
  },
  custom_datepicker: {
    '& .MuiOutlinedInput-root': {
      marginBottom: '15px'
    },
    '& .MuiOutlinedInput-adornedStart': {
      paddingLeft: '0'
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: '0'
    }
  },
  no_margin: {
    '& .MuiFormHelperText-contained': {
      marginLeft: '0',
      marginRight: '0'
    }
  },
  rootControl: {
    '& .MuiFormControlLabel-root': {
      width: 'fit-content',
      whiteSpace: 'nowrap',
    },
  },
  groupInput: {
    minWidth: '245px'
  }
});

const defaultCustomerGroups = [
  {
    id: 0,
    code: 'Not Logged In'
  }
];


const BasicCouponCode = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();

  const {
    storeActiveCategoriesWithActiveNavLevel,
    loadingCategories
  } = useSelector((state) => state.categories);
  const { profile, userStoreData } = useSelector((state) => state.user);
  const { store_products: { catalogProducts }, productsLoading } = useSelector((state) => state.products);
  const { customerGroups = [] } = useSelector((state) => state.customers);
  const { loadingGenerateCouponRule } = useSelector((state) => state.coupon_rules);

  const allCustomerGroups = [...defaultCustomerGroups, ...customerGroups];


  useEffect(() => {
    if (!storeActiveCategoriesWithActiveNavLevel.length) {
      getStoreCategoriesApi({dispatch});
    }

    if (!catalogProducts.length) {
      getStoreProductsApi({dispatch});
    }

    getStoreCustomerGroupsApi({dispatch});
  }, []);


  const getCurrentError = (values, attributeName, operator, errors, touched) => {
    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
      condition.attribute_name === attributeName && condition.operator === operator
    ));

    if (currentIndex === -1) {
      return false;
    }

    return touched.action_condition?.conditions[currentIndex] && errors.action_condition?.conditions[currentIndex]?.value;
  };

  const handleSave = async (values) => {
    const {sku, category_ids, quote_item_row_total, quote_item_qty, coupon_code, minOrder, from_date, to_date, allowEndDate, ...rest} = values;
    const couponRule = {
      ...rest,
      ...(from_date && { from_date }),
      ...(to_date && { to_date }),
      extension_attributes: {
        created_by: profile.isDermproStaff ? 'DermPRO staff' : `${profile.firstName} ${profile.lastName}`
      }
    };
    const isCouponGenerated = await createCouponRuleCombinedApi({
      dispatch,
      filterConditions: [
        {
          field: 'code',
          value: coupon_code.code,
          conditionType: 'eq'
        }
      ],
      couponRule,
      coupon_code
    });

    if (isCouponGenerated) {
      history.push('/configurations/coupon-manager');
    }
  };

  const changeSpecificCategoriesAppliesTo = (value, action, option, values, setFieldValue) => {
    const conditions = values.action_condition.conditions.map((condition) => {
      if (condition.attribute_name === 'category_ids' && condition.operator === '()') {
        if (action === 'select-option') {
          return {
            ...condition,
            value: condition.value.length > 1 ?
              [...condition.value.split(', '), String(option.option.id)].join(', ')
              : value.map((v) => v.id).join('')
          };
        }

        if (action === 'remove-option') {
          const filteredValues = condition.value.split(', ').filter((id) => id != option.option.id);
          return {
            ...condition,
            value: filteredValues.join(', ')
          };
        }
      }

      return condition;
    });

    setFieldValue('action_condition.conditions', conditions);
  };

  const onBlurSpecificCategoriesAppliesTo = (values, setFieldTouched) => {
    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
      condition.attribute_name === 'category_ids' && condition.operator === '()'
    ));

    setFieldTouched(`action_condition.conditions[${currentIndex}]`, true);
  };

  const onClickSpecificListingsAppliesTo =  (values, setFieldValue) => {
    const specificCategories = {
      ...values.sku,
      operator: '()',
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '{}' && cond.operator !== '()'
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, specificCategories]);
  };

  const changeSpecificListingsAppliesTo =  (value, action, option, values, setFieldValue) => {
    const conditions = values.action_condition.conditions.map((condition) => {
      if (condition.attribute_name === 'sku' && condition.operator === '()') {
        if (action === 'select-option') {
          return {
            ...condition,
            value: condition.value.length > 1 ?
              [...condition.value.split(', '), String(option.option.sku)].join(', ')
              : value.map((v) => v.sku).join('')
          };
        }

        if (action === 'remove-option') {
          const filteredValues = condition.value.split(', ').filter((sku) => sku != option.option.sku);
          return {
            ...condition,
            value: filteredValues.join(', ')
          };
        }
      }

      return condition;
    });

    setFieldValue('action_condition.conditions', conditions);
  };

  const onBlurSpecificListingsAppliesTo = (values, setFieldTouched) => {
    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
      condition.attribute_name === 'sku' && condition.operator === '()'
    ));

    setFieldTouched(`action_condition.conditions[${currentIndex}]`, true);
  };

  const onClickAllProductsApplyTo = (values, setFieldValue) => {
    const allProducts = {
      ...values.sku,
      value: 'PROD',
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '{}' && cond.operator !== '()'
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, allProducts]);
  };

  const onClickAllTreatmentsApplyTo = (values, setFieldValue) => {
    const allTreatments = {
      ...values.sku,
      value: 'TREAT'
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '{}' && cond.operator !== '()'
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, allTreatments]);
  };

  const onClickSpecificCategoriesNotApplyTo = (values, setFieldValue) => {
    const specificCategories = {
      ...values.category_ids,
      operator: '!()'
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '!()'
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, specificCategories]);
  };

  const changeSpecificCategoriesNotApplyTo = (value, action, option, values, setFieldValue) => {
    const conditions = values.action_condition.conditions.map((condition) => {
      if (condition.attribute_name === 'category_ids' && condition.operator === '!()') {
        if (action === 'select-option') {
          return {
            ...condition,
            value: condition.value.length > 1 ?
              [...condition.value.split(', '), String(option.option.id)].join(', ')
              : value.map((v) => v.id).join('')
          };
        }

        if (action === 'remove-option') {
          const filteredValues = condition.value.split(', ').filter((id) => id != option.option.id);
          return {
            ...condition,
            value: filteredValues.join(', ')
          };
        }
      }

      return condition;
    });

    setFieldValue('action_condition.conditions', conditions);
  };

  const onBlurSpecificCategoriesNotApplyTo = (values, setFieldTouched) => {
    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
      condition.attribute_name === 'category_ids' && condition.operator === '!()'
    ));

    setFieldTouched(`action_condition.conditions[${currentIndex}]`, true);
  };

  const onClickSpecificListingsNotApplyTo = (values, setFieldValue) => {
    const specificCategories = {
      ...values.sku,
      operator: '!()'
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '!()'
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, specificCategories]);
  };

  const changeSpecificListingsNotApplyTo =  (value, action, option, values, setFieldValue) => {
    const conditions = values.action_condition.conditions.map((condition) => {
      if (condition.attribute_name === 'sku' && condition.operator === '!()') {
        if (action === 'select-option') {
          return {
            ...condition,
            value: condition.value.length > 1 ?
              [...condition.value.split(', '), String(option.option.sku)].join(', ')
              : value.map((v) => v.sku).join('')
          };
        }

        if (action === 'remove-option') {
          const filteredValues = condition.value.split(', ').filter((sku) => sku != option.option.sku);
          return {
            ...condition,
            value: filteredValues.join(', ')
          };
        }
      }

      return condition;
    });

    setFieldValue('action_condition.conditions', conditions);
  };

  const onBlurSpecificListingsNotApplyTo =  (values, setFieldTouched) => {
    const currentIndex = values.action_condition?.conditions?.findIndex((condition) => (
      condition.attribute_name === 'sku' && condition.operator === '!()'
    ));

    setFieldTouched(`action_condition.conditions[${currentIndex}]`, true);
  };

  const changePurchaseAmount = (value, values, setFieldValue) => {
    const purchaseAmount = {
      ...values.quote_item_row_total,
      value: value.value
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '>='
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, purchaseAmount]);
  };

  const changeMinOrderQuontity = (value, values, setFieldValue) => {
    const productsQuontityParams = {
      ...values.quote_item_qty,
      value: value.value
    };
    const filteredConditions = values.action_condition.conditions.filter((cond) => (
      cond.operator !== '>='
    ));
    setFieldValue('action_condition.conditions', [...filteredConditions, productsQuontityParams]);
  };

  const changeSpecificCustomersGroup = (event, value, action, option, values, setFieldValue) => {
    event.preventDefault();

    if (action === 'select-option') {
      setFieldValue(
        'customer_group_ids',
        value !== null ?
          [...values.customer_group_ids, option.option.id]
          : values.customer_group_ids
      );
    }

    if (action === 'remove-option') {
      setFieldValue(
        'customer_group_ids',
        value !== null ?
          values.customer_group_ids.filter((id) => id !== option.option.id)
          : values.customer_group_ids
      );
    }
  };


  return (
    <div >
      <Formik
        initialValues={{
          is_active: true,
          name: '',
          description: '',
          use_auto_generation: false,
          coupon_code: {
            code: '',
            is_primary: true,
            type: 0
          },
          coupon_type: 'SPECIFIC_COUPON',
          uses_per_coupon: 0,
          uses_per_customer: 0,
          discount_amount: '',
          discount_step: '',
          discount_qty: '',
          simple_action: 'by_percent',
          minOrder: '',
          simple_free_shipping: '0',
          customer_group_ids: allCustomerGroups.map((g) => g.id),
          website_ids: userStoreData?.storeId ? [userStoreData.storeId] : [],
          allowEndDate: true,
          from_date: format(new Date(), 'yyyy-MM-dd'),
          to_date: format(new Date(), 'yyyy-MM-dd'),
          sort_order: 10,
          action_condition: {
            condition_type: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product\\Combine',
            conditions: [],
            aggregator_type: 'all',
            operator: null,
            value: '1'
          },
          sku: {
            attribute_name: 'sku',
            operator: '{}',
            value: '',
            condition_type: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product'
          },
          category_ids: {
            attribute_name: 'category_ids',
            operator: '()',
            value: '',
            condition_type: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product'
          },
          quote_item_row_total: {
            attribute_name: 'quote_item_row_total',
            operator: '>=',
            value: '',
            condition_type: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product'
          },
          quote_item_qty: {
            attribute_name: 'quote_item_qty',
            operator: '>=',
            value: '',
            condition_type: 'Magento\\SalesRule\\Model\\Rule\\Condition\\Product'
          }
        }}
        enableReinitialize
        validationSchema={
          yup.object({
            name: yup
              .string()
              .required('Field is required'),
            coupon_code: yup
              .object()
              .shape({
                code: yup
                  .string()
                  .required('Field is required'),
              }),
            uses_per_coupon: yup
              .number()
              .integer('Only integer numbers are allowed')
              .min(0, 'Only positive numbers are allowed'),
            discount_amount: yup
              .number()
              .notOneOf([0], 'Field is required')
              .when('simple_action', {
                is: (val) => val === 'eachn_perc',
                then: yup
                  .number()
                  .max(100, 'Invalid percent')
                  .required('Field is required')
              })
              .when('simple_action', {
                is: (val) => val === 'buy_x_get_y',
                then: yup
                  .number()
                  .notOneOf([0], 'Field is required')
                  .required('Field is required')
              })
              .required('Field is required'),
            discount_step: yup
              .number()
              .when('simple_action', {
                is: (val) => val === 'buy_x_get_y',
                then: yup
                  .number()
                  .min(1, 'Field is required')
                  .required('Field is required')
              }),
            customer_group_ids: yup
              .array()
              .min(1, 'Choose a customer group')
              .required('Field is required'),
            action_condition: yup
              .object()
              .shape({
                conditions: yup
                  .array()
                  .of(
                    yup.object().shape({
                      attribute_name: yup
                        .string()
                        .required('Field is required'),
                      operator: yup
                        .string()
                        .required('Field is required'),
                      value: yup
                        .string()
                        .required('Field is required')
                    }))
              }),
            from_date: yup
              .string()
              .nullable()
              .test(
                'from_date',
                'Start Date must be earlier than End Date',
                function (value) {
                  const { to_date } = this.parent;

                  if (!value || !to_date) {
                    return true;
                  }

                  return new Date(value) <= new Date(to_date);
                }
              ),
            to_date: yup
              .string()
              .nullable()
              .test(
                'to_date',
                'Start Date must be earlier than End Date',
                function (value) {
                  const { from_date } = this.parent;

                  if (!value || !from_date) {
                    return true;
                  }

                  return new Date(value) >= new Date(from_date);
                }
              ),
          })
        }
        onSubmit={handleSave}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched
        }) => {
          return (
            <form onSubmit={handleSubmit} noValidate className="max-w-1000 mx-auto">
              <Grid container spacing={3} className="mt-5">
                <Grid item xs={12} md={8}>
                  <Card className="p-5 mb-4" elevation={0}>
                    <Typography variant="h5" className="font-bold mb-5">
                      Title <span className="text-error">*</span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      value={values.name}
                      className={`${classes.no_margin} ${classes.custom_input}`}
                      name="name"
                      onChange={(event) => {
                        handleChange(event);
                        setFieldValue('name', event.target.value);
                      }}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Card>
                  <Card className="p-5 mb-4" elevation={0}>
                    <Typography variant="h5" className="font-bold mb-5">
                      Description
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      value={values.description}
                      className={`${classes.no_margin} ${classes.custom_input}`}
                      name="description"
                      onChange={(event) => {
                        handleChange(event);
                        setFieldValue('description', event.target.value);
                      }}
                      onBlur={handleBlur}
                    />
                  </Card>
                  <Card className="p-5 mb-4" elevation={0}>
                    <div className="flex items-center">
                      <div className="whitespace-no-wrap">Coupon Code <span className="text-error">*</span></div>
                      <TextField
                        required
                        fullWidth
                        variant="outlined"
                        value={values.coupon_code.code}
                        className={`ml-2 ${classes.custom_input}`}
                        name="coupon_code"
                        onChange={(event) => {
                          handleChange(event);
                          setFieldValue('coupon_code', {...values.coupon_code, code: event.target.value});
                        }}
                        onBlur={handleBlur}
                        error={touched.coupon_code && Boolean(errors.coupon_code?.code)}
                        helperText={touched.coupon_code && errors.coupon_code?.code}
                      />
                    </div>
                  </Card>
                  <Card className="p-5 mb-4" elevation={0}>
                    <Typography variant="h5" className="font-bold mb-5">
                      Coupon Usage
                    </Typography>
                    <div className="flex items-center mb-5">
                      <div className="whitespace-no-wrap">Uses per Coupon</div>
                      <NumberFormat
                        customInput={TextField}
                        fullWidth
                        allowNegative={false}
                        decimalScale={0}
                        size="small"
                        variant="outlined"
                        name="uses_per_coupon"
                        className={`ml-2 mr-5 ${classes.custom_input}`}
                        value={values.uses_per_coupon}
                        onValueChange={(value) => {
                          setFieldValue('uses_per_coupon', value.floatValue);
                        }}
                        error={touched.uses_per_coupon && Boolean(errors.uses_per_coupon)}
                        helperText={touched.uses_per_coupon && errors.uses_per_coupon}
                      />
                    </div>
                    <div className="flex items-center">
                      <div className="whitespace-no-wrap">Uses per Customer</div>
                      <NumberFormat
                        customInput={TextField}
                        fullWidth
                        allowNegative={false}
                        decimalScale={0}
                        size="small"
                        variant="outlined"
                        name="uses_per_customer"
                        className={`ml-2 ${classes.custom_input}`}
                        value={values.uses_per_customer}
                        onValueChange={(value) => {
                          setFieldValue('uses_per_customer', value.floatValue);
                          setFieldValue('customer_group_ids', []);
                        }}
                        error={touched.uses_per_customer && Boolean(errors.uses_per_customer)}
                        helperText={touched.uses_per_customer && errors.uses_per_customer}
                      />
                      <InfoTooltip title="Purchaser must be logged into an account to use the coupon code" />
                    </div>
                  </Card>
                  <Card className="p-5 mb-4" elevation={0}>
                    <Typography variant="h5" className="font-bold mb-5">
                      Discount Type
                    </Typography>
                    <FormControl component="div" className={`${classes.rootControl} mb-3`}>
                      <RadioGroup
                        name="simple_action"
                        value={values.simple_action}
                        onChange={(event) => {
                          setFieldValue('simple_action', event.target.value);
                        }}
                      >
                        <FormControlLabel
                          value="by_percent"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              onClick={() => {
                                if (values.simple_action != 'by_percent') {
                                  setFieldValue('discount_amount', '');
                                  setFieldTouched('discount_amount', false);
                                }
                              }}
                            />}
                          label={
                            <div className="flex items-center">
                              <span>Percentage OFF</span>
                              {values.simple_action === 'by_percent' &&
                              <NumberFormat
                                customInput={TextField}
                                suffix={'%'}
                                required
                                allowNegative={false}
                                size="small"
                                label="Discount"
                                variant="outlined"
                                name="by_percent.discount_amount"
                                className={`ml-4 w-5 ${classes.custom_input}`}
                                value={values.discount_amount}
                                onValueChange={(value) => {
                                  setFieldValue('discount_amount', value.floatValue);
                                }}
                                onBlur={(event) => {
                                  handleBlur(event);
                                  setFieldValue('discount_amount', +event.target.value.replace('%', ''));
                                }}
                                error={touched.discount_amount && Boolean(errors.discount_amount)}
                                helperText={touched.discount_amount && errors.discount_amount}
                              />
                              }
                            </div>
                          }
                        />
                        <FormControlLabel
                          value="by_fixed"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              onClick={() => {
                                if (!['by_fixed', 'cart_fixed'].includes(values.simple_action)) {
                                  setFieldValue('discount_amount', '');
                                  setFieldTouched('discount_amount', false);
                                }
                              }}
                              checked={values.simple_action === 'by_fixed' || values.simple_action === 'cart_fixed'}
                            />}
                          label={
                            <div className="flex items-center">
                              <span>Dollar OFF</span>
                              {(values.simple_action === 'by_fixed' || values.simple_action === 'cart_fixed') &&
                              <NumberFormat
                                customInput={TextField}
                                prefix={'$'}
                                required
                                allowNegative={false}
                                size="small"
                                label="Discount"
                                variant="outlined"
                                thousandsGroupStyle="thousand"
                                thousandSeparator
                                name="dollarOf.discount_amount"
                                className={`ml-4 w-5 ${classes.custom_input}`}
                                value={values.discount_amount}
                                onValueChange={(value) => {
                                  setFieldValue('discount_amount', value.floatValue);
                                }}
                                error={touched.discount_amount && Boolean(errors.discount_amount)}
                                helperText={touched.discount_amount && errors.discount_amount}
                              />
                              }
                            </div>
                          }
                        />
                        {(values.simple_action === 'by_fixed' || values.simple_action === 'cart_fixed') &&
                          <div className="ml-5">
                            <FormControlLabel
                              value="by_fixed"
                              control={
                                <Radio
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setFieldTouched('discount_amount', false);
                                  }}
                                />}
                              label={
                                <div className="flex items-center">Each Item</div>
                              }
                            />
                            <FormControlLabel
                              value="cart_fixed"
                              control={
                                <Radio
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setFieldTouched('discount_amount', false);
                                  }}
                                />}
                              label={
                                <div className="flex items-center">Whole Cart</div>
                              }
                            />
                          </div>
                        }
                        <FormControlLabel
                          value="eachn_fixprice"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              onClick={() => {
                                if (values.simple_action != 'eachn_fixprice') {
                                  setFieldValue('discount_amount', '');
                                  setFieldTouched('discount_amount', false);
                                }
                              }}
                            />}
                          label={
                            <div className="flex items-center">
                              <span>Fixed Price</span>
                              {values.simple_action === 'eachn_fixprice' &&
                              <NumberFormat
                                customInput={TextField}
                                prefix={'$'}
                                required
                                allowNegative={false}
                                size="small"
                                label="Price"
                                variant="outlined"
                                thousandsGroupStyle="thousand"
                                thousandSeparator
                                name="eachn_fixprice.discount_amount"
                                className={`ml-4 w-5 ${classes.custom_input}`}
                                value={values.discount_amount}
                                onValueChange={(value) => {
                                  setFieldValue('discount_amount', value.floatValue);
                                }}
                                error={touched.discount_amount && Boolean(errors.discount_amount)}
                                helperText={touched.discount_amount && errors.discount_amount}
                              />
                              }
                            </div>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                    <div className="flex items-center">
                      <div className="flex max-w-100">
                        <NumberFormat
                          customInput={TextField}
                          fullWidth
                          allowNegative={false}
                          size="small"
                          variant="outlined"
                          name="discount_qty"
                          className={classes.custom_input}
                          value={values.discount_qty}
                          onValueChange={(value) => {
                            setFieldValue('discount_qty', value.floatValue);
                          }}
                          onBlur={(event) => {
                            handleBlur(event);
                          }}
                        />
                      </div>
                      <div className="whitespace-no-wrap text-gray ml-2">
                        Maximum quantity allowed to receive the discount
                      </div>
                    </div>
                    <Divider className="my-5" />
                    <FormControl className={classes.rootControl}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.simple_free_shipping == '2'}
                            name="simple_free_shipping"
                            color="primary"
                            onChange={(event) => setFieldValue('simple_free_shipping', event.target.checked ? '2' : '0')}
                          />}
                        label="Free Shipping"
                      />
                    </FormControl>
                  </Card>

                  <Card className="p-5 mt-4" elevation={0}>
                    <Typography variant="h5" className="font-bold mb-5">
                      Applies To
                    </Typography>
                    <FormControl component="fieldset" className={classes.rootControl}>
                      <RadioGroup
                        aria-label="applies to"
                      >
                        <FormControlLabel
                          value="Storewide"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!values.action_condition.conditions.some((cond) => cond.operator === '()' || cond.operator === '{}')}
                              onClick={() => {
                                const filteredConditions = values.action_condition.conditions.filter((cond) => cond.operator !== '()' && cond.operator !== '{}');
                                setFieldValue('action_condition.conditions', filteredConditions);
                              }}
                            />
                          }
                          label="Storewide"
                        />
                        <FormControlLabel
                          value="All Products"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'sku' && cond.operator === '{}' && cond.value === 'PROD'
                              )) || false}
                              onClick={() => (onClickAllProductsApplyTo(values, setFieldValue))}
                            />
                          }
                          label="All Products"
                        />
                        <FormControlLabel
                          value="All Treatments"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'sku' && cond.operator === '{}' && cond.value === 'TREAT'
                              )) || false}
                              onClick={() => onClickAllTreatmentsApplyTo(values, setFieldValue)}
                            />
                          }
                          label="All Treatments"
                        />
                        <FormControlLabel
                          value="Specific Categories"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'category_ids' && cond.operator === '()'
                              )) || false}
                              onClick={() => {
                                const filteredConditions = values.action_condition.conditions.filter((cond) => (
                                  cond.operator !== '{}' && cond.operator !== '()'
                                ));
                                setFieldValue('action_condition.conditions', [...filteredConditions, values.category_ids]);
                              }}
                            />
                          }
                          label="Specific Categories"
                        />
                        {values.action_condition.conditions.find((cond) => (
                          cond.attribute_name === 'category_ids' && cond.operator === '()'
                        )) &&
                          <>
                            {loadingCategories ?
                              <div className="my-5">
                                <span className="text-muted">Preparing Categories Data...</span>
                                <LinearProgress />
                              </div>
                              :
                              <Autocomplete
                                multiple
                                fullWidth
                                disableClearable
                                size="small"
                                className="my-5"
                                name="action_condition.conditions"
                                defaultValue={values.action_condition.conditions.find((cond) => (
                                  cond.attribute_name === 'category_ids' && cond.operator === '()'
                                )).value || []}
                                options={storeActiveCategoriesWithActiveNavLevel}
                                getOptionLabel={(option) => option.nameWithPath}
                                onChange={(_, value, action, option) => changeSpecificCategoriesAppliesTo(value, action, option, values, setFieldValue)}
                                onBlur={() => onBlurSpecificCategoriesAppliesTo(values, setFieldTouched)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Categories"
                                    required
                                    error={Boolean(getCurrentError(values, 'category_ids', '()', errors, touched))}
                                    helperText={getCurrentError(values, 'category_ids', '()', errors, touched)}
                                  />
                                )}
                                renderOption={(option, { inputValue }) => {
                                  const matches = match(option?.nameWithPath, inputValue);
                                  const parts = parse(option?.nameWithPath, matches);
                                  return (
                                    <div>
                                      {parts.map((part, index) => (
                                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                                          {part.text}
                                        </span>
                                      ))}
                                    </div>
                                  );
                                }}
                              />
                            }
                          </>
                        }
                        <FormControlLabel
                          value="Specific Listings"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'sku' && cond.operator === '()'
                              )) || false}
                              onClick={() => onClickSpecificListingsAppliesTo(values, setFieldValue)}
                            />
                          }
                          label="Specific Listings"
                        />
                        {values.action_condition.conditions.find((cond) => (
                          cond.attribute_name === 'sku' && cond.operator === '()'
                        ))  &&
                          <>
                            {productsLoading ?
                              <div className="my-5">
                                <span className="text-muted">Preparing Listings Data...</span>
                                <LinearProgress />
                              </div>
                              :
                              <Autocomplete
                                multiple
                                fullWidth
                                disableClearable
                                noOptionsText={
                                  <Link
                                    to="/request-sku" target="_blank" onMouseDown={() => {
                                      window.open('/request-sku', '_blank');
                                    }}
                                  >
                                    Request Listings
                                  </Link>
                                }
                                size="small"
                                className="my-5"
                                defaultValue={values.action_condition.conditions.find((cond) => (
                                  cond.attribute_name === 'sku' && cond.operator === '()'
                                )).value || []}
                                options={catalogProducts}
                                getOptionLabel={(option) => option.name}
                                onChange={(_, value, action, option) => changeSpecificListingsAppliesTo(value, action, option, values, setFieldValue)}
                                onBlur={() => onBlurSpecificListingsAppliesTo(values, setFieldTouched)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Listings"
                                    required
                                    error={Boolean(getCurrentError(values, 'sku', '()', errors, touched))}
                                    helperText={getCurrentError(values, 'sku', '()', errors, touched)}
                                  />
                                )}
                                renderOption={(option, { inputValue }) => {
                                  const matches = match(option.name, inputValue);
                                  const parts = parse(option.name, matches);
                                  return (
                                    <div>
                                      {parts.map((part, index) => (
                                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                                          {part.text}
                                        </span>
                                      ))}
                                    </div>
                                  );
                                }}
                              />}
                          </>
                        }
                      </RadioGroup>
                    </FormControl>
                    <Divider className="my-5" />
                    <Typography variant="h5" className="font-bold mb-5">
                      Does Not Apply To
                    </Typography>
                    <FormControl component="fieldset" className={classes.rootControl}>
                      <RadioGroup
                        aria-label="does not apply to"
                      >
                        <FormControlLabel
                          value="None"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={Boolean(!values.action_condition.conditions.filter((cond) => cond.operator === '!()').length)}
                              onClick={() => {
                                const conditionsWithoutExcludes = values.action_condition.conditions.filter((cond) => cond.operator !== '!()');
                                setFieldValue('action_condition.conditions', conditionsWithoutExcludes);
                              }}
                            />
                          }
                          label="None"
                        />
                        <FormControlLabel
                          value="Specific Categories"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'category_ids' && cond.operator === '!()'
                              )) || false}
                              onClick={() => onClickSpecificCategoriesNotApplyTo(values, setFieldValue)}
                            />
                          }
                          label="Specific Categories"
                        />
                        {values.action_condition.conditions.find((cond) => (
                          cond.attribute_name === 'category_ids' && cond.operator === '!()'
                        )) &&
                          <>
                            {loadingCategories ?
                              <div className="my-5">
                                <span className="text-muted">Preparing Categories Data...</span>
                                <LinearProgress />
                              </div>
                              :
                              <Autocomplete
                                multiple
                                fullWidth
                                disableClearable
                                size="small"
                                className="my-5"
                                defaultValue={values.action_condition.conditions.find((cond) => (
                                  cond.attribute_name === 'category_ids' && cond.operator === '!()'
                                )).value || []}
                                options={storeActiveCategoriesWithActiveNavLevel}
                                getOptionLabel={(option) => option.nameWithPath}
                                onChange={(_, value, action, option) => changeSpecificCategoriesNotApplyTo(value, action, option, values, setFieldValue)}
                                onBlur={() => onBlurSpecificCategoriesNotApplyTo(values, setFieldTouched)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Categories"
                                    required
                                    error={Boolean(getCurrentError(values, 'category_ids', '!()', errors, touched))}
                                    helperText={getCurrentError(values, 'category_ids', '!()', errors, touched)}
                                  />
                                )}
                                renderOption={(option, { inputValue }) => {
                                  const matches = match(option?.nameWithPath, inputValue);
                                  const parts = parse(option?.nameWithPath, matches);
                                  return (
                                    <div>
                                      {parts.map((part, index) => (
                                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                                          {part.text}
                                        </span>
                                      ))}
                                    </div>
                                  );
                                }}
                              />
                            }
                          </>
                        }
                        <FormControlLabel
                          value="Specific Listings"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={!!values.action_condition.conditions.find((cond) => (
                                cond.attribute_name === 'sku' && cond.operator === '!()'
                              )) || false}
                              onClick={() => onClickSpecificListingsNotApplyTo(values, setFieldValue)}
                            />
                          }
                          label="Specific Listings"
                        />
                        {values.action_condition.conditions.find((cond) => (
                          cond.attribute_name === 'sku' && cond.operator === '!()'
                        ))  &&
                          <>
                            {productsLoading ?
                              <div className="my-5">
                                <span className="text-muted">Preparing Listings Data...</span>
                                <LinearProgress />
                              </div>
                              :
                              <Autocomplete
                                multiple
                                fullWidth
                                disableClearable
                                noOptionsText={
                                  <Link
                                    to="/request-sku" target="_blank" onMouseDown={() => {
                                      window.open('/request-sku', '_blank');
                                    }}
                                  >
                                    Request Listings
                                  </Link>
                                }
                                size="small"
                                className="my-5"
                                defaultValue={values.action_condition.conditions.find((cond) => (
                                  cond.attribute_name === 'sku' && cond.operator === '!()'
                                )).value || []}
                                options={catalogProducts}
                                getOptionLabel={(option) => option.name}
                                onChange={(_, value, action, option) => changeSpecificListingsNotApplyTo(value, action, option, values, setFieldValue)}
                                onBlur={() => onBlurSpecificListingsNotApplyTo(values, setFieldTouched)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Listings"
                                    required
                                    error={Boolean(getCurrentError(values, 'sku', '!()', errors, touched))}
                                    helperText={getCurrentError(values, 'sku', '!()', errors, touched)}
                                  />
                                )}
                                renderOption={(option, { inputValue }) => {
                                  const matches = match(option.name, inputValue);
                                  const parts = parse(option.name, matches);
                                  return (
                                    <div>
                                      {parts.map((part, index) => (
                                        <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                                          {part.text}
                                        </span>
                                      ))}
                                    </div>
                                  );
                                }}
                              />}
                          </>
                        }
                      </RadioGroup>
                    </FormControl>
                  </Card>
                  <Card className="p-5 mt-4" elevation={0}>
                    <Typography variant="h5" className="font-bold mb-5">
                      Minimum Order to Qualify <span className="text-error">*</span>
                    </Typography>
                    <div className="flex items-center mb-5">
                      <FormControl component="fieldset" id="Min-order" className={classes.rootControl}>
                        <RadioGroup
                          aria-label="Min-order"
                          name="Minimum order"
                          value={values.minOrder}
                          onChange={(event) => {
                            setFieldValue('minOrder', event.target.value);
                          }}
                        >
                          <FormControlLabel
                            value="None"
                            control={
                              <Radio
                                size="small"
                                color="primary"
                                checked={Boolean(!values.action_condition.conditions.filter((cond) => cond.operator === '>=').length)}
                                onClick={() => {
                                  const conditionsWithoutExcludes = values.action_condition.conditions.filter((cond) => cond.operator !== '>=');
                                  setFieldValue('action_condition.conditions', conditionsWithoutExcludes);
                                }}
                              />
                            }
                            label="None"
                          />
                          <FormControlLabel
                            value="PurchaseAmount"
                            control={
                              <Radio
                                size="small"
                                color="primary"
                                checked={!!values.action_condition.conditions.find((cond) => (
                                  cond.attribute_name === 'quote_item_row_total' && cond.operator === '>='
                                )) || false}
                                onClick={() => {
                                  const filteredConditions = values.action_condition.conditions.filter((cond) => (
                                    cond.operator !== '>='
                                  ));
                                  setFieldValue('action_condition.conditions', [...filteredConditions, values.quote_item_row_total]);
                                }}
                              />
                            }
                            label={
                              <div className="flex items-center">
                                <span>Purchase Amount</span>
                                {!!values.action_condition.conditions.find((cond) => (
                                  cond.attribute_name === 'quote_item_row_total' && cond.operator === '>='
                                )) &&
                                  <NumberFormat
                                    customInput={TextField}
                                    prefix={'$'}
                                    fullWidth
                                    allowNegative={false}
                                    size="small"
                                    variant="outlined"
                                    name="minPurchaseAmount"
                                    className={`ml-4 max-w-100 ${classes.custom_input}`}
                                    onValueChange={(value) => changePurchaseAmount(value, values, setFieldValue)}
                                  />
                                }
                              </div>
                            }
                          />
                          <FormControlLabel
                            value="MinOrderQuontity"
                            control={
                              <Radio
                                size="small"
                                color="primary"
                                checked={!!values.action_condition.conditions.find((cond) => (
                                  cond.attribute_name === 'quote_item_qty' && cond.operator === '>='
                                )) || false}
                                onClick={() => {
                                  const filteredConditions = values.action_condition.conditions.filter((cond) => (
                                    cond.operator !== '>='
                                  ));
                                  setFieldValue('action_condition.conditions', [...filteredConditions, values.quote_item_qty]);
                                }}
                              />
                            }
                            label={
                              <div className="flex items-center">
                                <span>Quantity</span>
                                {!!values.action_condition.conditions.find((cond) => (
                                  cond.attribute_name === 'quote_item_qty' && cond.operator === '>='
                                )) &&
                                  <NumberFormat
                                    customInput={TextField}
                                    fullWidth
                                    allowNegative={false}
                                    size="small"
                                    variant="outlined"
                                    name="minOrderQuontity"
                                    className={`ml-4 max-w-100 ${classes.custom_input}`}
                                    onValueChange={(value) => changeMinOrderQuontity(value, values, setFieldValue)}
                                  />
                                }
                              </div>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    </div>
                  </Card>
                  <Card className="p-5 mt-4" elevation={0}>
                    <Typography variant="h5" className="font-bold mb-5">
                      Customer Eligibility <span className="text-error">*</span>
                    </Typography>
                    <FormControl component="fieldset" className={classes.rootControl}>
                      <RadioGroup
                        aria-label="applies to customers"
                      >
                        <FormControlLabel
                          value="Everyone"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              disabled={!!values.uses_per_customer}
                              checked={values.customer_group_ids.length === allCustomerGroups.length}
                              onClick={() => setFieldValue('customer_group_ids', allCustomerGroups.map((g) => g.id))}
                            />}
                          label="Everyone"
                        />
                        <FormControlLabel
                          value="Specific group of customers"
                          control={
                            <Radio
                              size="small"
                              color="primary"
                              checked={values.customer_group_ids.length !== allCustomerGroups.length}
                              onClick={() => setFieldValue('customer_group_ids', [])}
                            />}
                          label={
                            <div className="whitespace-no-wrap">Specific group of customers</div>
                          }
                        />
                      </RadioGroup>
                      {values.customer_group_ids.length !== allCustomerGroups.length &&
                      <Autocomplete
                        required
                        multiple
                        fullWidth
                        size="small"
                        className={classes.groupInput}
                        name="customer_group_ids"
                        defaultValue={values.customer_group_ids}
                        options={allCustomerGroups}
                        getOptionLabel={(option) => option.code}
                        getOptionDisabled={(option) => !!values.uses_per_customer && option.id === 0}
                        onChange={(event, value, action, option) => changeSpecificCustomersGroup(event, value, action, option, values, setFieldValue)}
                        onBlur={() => setFieldTouched('customer_group_ids', true)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            variant="outlined"
                            placeholder="Choose customer group"
                            error={touched.customer_group_ids && Boolean(errors.customer_group_ids)}
                            helperText={touched.customer_group_ids && errors.customer_group_ids}
                          />
                        )}
                        renderTags={(tagValue) =>
                          tagValue.map((option, index) => (
                            <Chip
                              key={index}
                              label={option.code}
                              disabled={!!values.uses_per_customer && option.id === 0}
                            />
                          ))
                        }
                      />
                      }
                    </FormControl>
                  </Card>
                  <Card className="p-5 mt-4" elevation={0}>
                    <div>
                      <Typography variant="h5" className="font-bold mb-2">
                        Active Dates
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            name="allowEndDate"
                            color="primary"
                            checked={values.allowEndDate}
                            onChange={() => {
                              setFieldValue('allowEndDate', !values.allowEndDate);
                              setFieldValue('to_date', values.allowEndDate ? values.to_date : '');
                            }}
                          />
                        }
                        label="Set an End Date"
                      />
                    </div>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <DatePicker
                        autoOk
                        variant="inline"
                        inputVariant="outlined"
                        format="MM/dd/yyyy"
                        emptyLabel="mm/dd/yyyy"
                        name="from_date"
                        label="Start Date"
                        className="mt-5 mr-5"
                        value={values.from_date && isValid(parseISO(values.from_date)) ? parseISO(values.from_date) : null}
                        onChange={(date) => {
                          setFieldValue('from_date', format(date, 'yyyy-MM-dd'));
                        }}
                        onBlur={() => {
                          setFieldTouched('from_date', true);
                        }}
                        error={touched.from_date && Boolean(errors.from_date)}
                        helperText={touched.from_date && errors.from_date}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <CalendarTodayIcon className="text-muted text-16" />
                          ),
                          endAdornment: (
                            <Icon
                              className="cursor-pointer"
                              onClick={(event) => {
                                event.stopPropagation();
                                setFieldValue('from_date', null);
                              }
                              }
                            >
                              close
                            </Icon>
                          )
                        }}
                      />
                      {values.allowEndDate &&
                        <DatePicker
                          autoOk
                          variant="inline"
                          format="MM/dd/yyyy"
                          emptyLabel="mm/dd/yyyy"
                          name="to_date"
                          label="End Date"
                          className="mt-5"
                          value={values.to_date && isValid(parseISO(values.to_date)) ? parseISO(values.to_date) : null}
                          onChange={(date) => {
                            setFieldValue('to_date', format(date, 'yyyy-MM-dd'));
                          }}
                          onBlur={() => setFieldTouched('to_date', true)}
                          inputVariant="outlined"
                          error={touched.to_date && Boolean(errors.to_date)}
                          helperText={touched.to_date && errors.to_date}
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <CalendarTodayIcon className="text-muted text-16" />
                            ),
                            endAdornment: (
                              <Icon
                                className="cursor-pointer"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setFieldValue('to_date', null);
                                  setFieldValue('allowEndDate', false);
                                }
                                }
                              >
                                close
                              </Icon>
                            )
                          }}
                        />
                      }
                    </MuiPickersUtilsProvider>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4} className="relative">
                  <Card className="p-5 sticky" elevation={0}>
                    <CouponRuleSummary
                      values={values}
                      categories={storeActiveCategoriesWithActiveNavLevel}
                      products={catalogProducts}
                      customerGroups={allCustomerGroups}
                    />
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    className="my-4"
                    onClick={() => {
                      if (Object.keys(errors).length) {
                        dispatch(showNotification('error', 'Required fields are missing'));
                      }
                    }}
                  >
                    Create Coupon Rule
                  </Button>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>
      <BackdropCircular loading={loadingGenerateCouponRule} />
    </div>
  );
};

export default BasicCouponCode;
